import { localizedStrings } from '@core/strings'
import { Entity } from '@core/entities/entity'
import { IPermitTask } from './IPermitTask'
import { plainToInstance, Type } from 'class-transformer'
import {
  PermitTaskPermittedActions,
  PermitTaskStatus,
  PermitTaskType,
} from './types'
import { User } from '@core/entities/user'
import { statuses } from '@core/theme/statuses'

export class PermitTask extends Entity<IPermitTask> {
  id: number

  name: string

  type: PermitTaskType

  status: PermitTaskStatus

  ordinal: number

  permitId: number

  @Type(() => User)
  assignedTo: User

  @Type(() => User)
  doneBy?: User

  organisationId: number

  projectId: number

  checklistTemplateId?: number

  checklistId?: number
  createdOn: string

  modifiedOn: string

  permittedActions: Array<PermitTaskPermittedActions>

  permitTemplateVersionRoleId?: number
  permitTemplateVersionRoleName?: string
  permitTemplateVersionRoleGroupIds?: Array<number>

  earliestCompletionAt?: string | null

  public static new(payload: unknown): PermitTask {
    const entity = plainToInstance(PermitTask, payload)

    return entity
  }

  public static getNumberOfTasksByStatus(
    tasks: IPermitTask[],
    status: PermitTaskStatus,
  ): number {
    return tasks?.filter((task) => task.status === status).length
  }

  public getScheduleDurationLabel(permitStartsOn: string): string {
    if (!this.earliestCompletionAt) {
      return ''
    }

    const hours = Math.round(
      (new Date(permitStartsOn).getTime() -
        new Date(this.earliestCompletionAt).getTime()) /
        (1000 * 60 * 60),
    )

    if (hours >= 24 && hours % 24 === 0) {
      const days = hours / 24
      return `${days} ${
        days === 1
          ? localizedStrings.day.toLowerCase()
          : localizedStrings.days.toLowerCase()
      }`
    }

    return `${hours} ${
      hours === 1
        ? localizedStrings.hour.toLowerCase()
        : localizedStrings.hours.toLowerCase()
    }`
  }

  public checkTaskType(type: PermitTaskType): boolean {
    return this.type === type
  }

  public checkTaskStatus(status: PermitTaskStatus): boolean {
    return this.status === status
  }

  public checkForMultipleTaskTypes(types: PermitTaskType[]): boolean {
    return types.includes(this.type)
  }

  public checkForMultipleTaskStatuses(statuses: PermitTaskStatus[]): boolean {
    return statuses.includes(this.status)
  }

  public isLockedBySchedule(): boolean {
    if (this.status !== 'TO_DO' || !this.earliestCompletionAt) {
      return false
    }

    const now = new Date().toISOString()
    return now < this.earliestCompletionAt
  }

  public getStatusText(status: PermitTaskStatus): string {
    switch (status) {
      case 'TO_DO':
        return this.isLockedBySchedule()
          ? localizedStrings.scheduled
          : localizedStrings.toDo
      case 'DONE':
        return statuses.completed.label
      case 'CANCELLED':
        return statuses.cancelled.label
      default:
        return ''
    }
  }

  public getStatusColor() {
    if (this.isLockedBySchedule()) {
      return 'default'
    }

    switch (this.status) {
      case 'TO_DO':
        return 'warning'
      case 'DONE':
        return statuses.completed.color
      case 'CANCELLED':
        return statuses.cancelled.color
      default:
        return 'warning'
    }
  }

  public hasPermissionsToCompleteAction() {
    return this.permittedActions.includes('DO_TASK')
  }
}
